import { hvacUpload } from "api/admin";
import { useRef, useState } from "react";
import { setLoading } from "store/appSlice";
import GenericCard from "widgets/cards/generic";
import SelectFile from "widgets/shared/selectFile";
import * as XLSX from "xlsx";
import BulkUploadSvg from "../../../../assets/svg/bulk-upload.svg";
import { SecondaryHeightLayout } from "hooks/viewport-dimensions";
import HvacUploadForm, {
  HvacUploadFormType,
} from "widgets/forms/support/hvacUpload";
import { useTranslation } from "react-i18next";

const HvacReport = () => {
  const { t } = useTranslation();
  const [jsonData, setJsonData] = useState(null);
  const [newData, setNewJsonData] = useState(null);
  const fileInputRef = useRef(null); // Add a reference to the file input element

  const handleFileUpload = (file: any) => {
    if (file) {
      const reader = new FileReader();

      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });

        // Assuming first sheet is the main sheet
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        // Convert sheet to JSON
        const parsedData = XLSX.utils.sheet_to_json(sheet);
        const processedData = processParsedData(parsedData);
        const fData = furtherProcessData(processedData);

        setJsonData(fData);
      };

      reader.readAsArrayBuffer(file);
    }
  };
  const processParsedData = (data: any[]) => {
    // Get all unique keys from the data
    const allKeys = Array.from(
      new Set(data.flatMap((item) => Object.keys(item)))
    );

    return data.map((item) => {
      const newItem: any = {};
      allKeys.forEach((key) => {
        const newKey = key.toLowerCase().replace("(optional)", "").trim();
        let newValue = item[key];
        if (typeof newValue === "string") {
          newValue = newValue.replace("(optional)", "").trim();
        }
        newItem[newKey] =
          newValue !== undefined && newValue !== null ? String(newValue) : "";
      });
      return newItem;
    });
  };

  const furtherProcessData = (data: any[]) => {
    // Skip first and last item
    return data.slice(1, -1).map((item) => ({
      address: item["indoor unit address"] || "",
      name: item["indoor unit name"] || "",
      SUM: item["used power consumption (kwh)"] || "",
    }));
  };

  const doHvacUpload = async (form: HvacUploadFormType) => {
    setLoading(true);
    setNewJsonData(form);
    const { data, status, isLoading } = await hvacUpload(form.lines);
    setLoading(isLoading);

    if (status && status === 200) {
      setJsonData(data);
    }
  };

  const clearData = () => {
    setJsonData(null);
    setNewJsonData(null);
    if (fileInputRef.current) {
      fileInputRef.current.clear(); // Use the clear method to reset the file input element's value
    }
  };

  return (
    <SecondaryHeightLayout>
      <GenericCard extra="h-full" title={t("support.hvac.report.upload")}>
        <div className="flex flex-col gap-6">
          <SelectFile
            id={"hvacuploadxlsx"}
            onChange={handleFileUpload}
            sizes="sm"
            ref={fileInputRef}
            color="secondary"
          />
          {jsonData && jsonData.length > 0 ? (
            <>
              <HvacUploadForm
                submit={doHvacUpload}
                clearData={clearData}
                onClose={function (): void {}}
                values={{
                  lines: jsonData,
                }}
              />
              <div className={`mb-2 grid grid-cols-1 gap-2 md:grid-cols-2`}>
                <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-md bg-gray-100 p-4">
                  {JSON.stringify(jsonData, null, 2)}
                </pre>
                <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-md bg-gray-100 p-4">
                  {newData && newData.devices
                    ? JSON.stringify(newData.devices, null, 2)
                    : ""}
                </pre>
              </div>
            </>
          ) : (
            <div className="mt-12 flex justify-center">
              <img
                src={BulkUploadSvg}
                alt="Modal content"
                style={{ width: "20%" }}
              />
            </div>
          )}
        </div>
      </GenericCard>
    </SecondaryHeightLayout>
  );
};

export default HvacReport;
